import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { TrendUp, TrendDown } from "@phosphor-icons/react";
import api from "@/lib/api";

const monthName = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "numeric" });

export default function Rates() {
  const [list, setList] = useState([]);          // commodities with real rates, most widely reported first
  const [commodity, setCommodity] = useState("");
  const [trend, setTrend] = useState(null);
  const [months, setMonths] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/rates/state-commodities");
        const names = (data.items || []).map((i) => i.commodity);
        setList(names);
        if (names.length) setCommodity(names[0]);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!commodity) return;
    let live = true;
    setTrend(null); setMonths(null);
    (async () => {
      try {
        const [t, m] = await Promise.all([
          api.get("/rates/trends", { params: { commodity } }),
          api.get("/rates/season-report", { params: { commodity } }),
        ]);
        if (live) { setTrend(t.data); setMonths(m.data); }
      } catch {}
    })();
    return () => { live = false; };
  }, [commodity]);

  const chartData = (trend?.points || []).map((p) => ({
    d: new Date(p.recorded_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
    v: p.avg_rate,
  }));
  const last = chartData[chartData.length - 1]?.v || 0;
  const first = chartData[0]?.v || 0;
  const pct = first ? ((last - first) / first) * 100 : 0;
  const tabs = useMemo(() => list.slice(0, 8), [list]);

  return (
    <div className="space-y-6" data-testid="rates-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-stone-500">Rate Intelligence</div>
          <div className="font-display text-2xl font-bold text-stone-900">Aaj Ka Bhav · Trends</div>
          <div className="text-xs text-stone-500 mt-1">Daily average of the mandis that reported to AGMARKNET (data.gov.in), ₹ per quintal.</div>
        </div>
        <div className="flex flex-wrap items-center gap-1 rounded-md border border-stone-200 bg-white p-1">
          {tabs.map((c) => (
            <button key={c} onClick={() => setCommodity(c)} className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${commodity === c ? "bg-emerald-900 text-white" : "text-stone-700 hover:bg-stone-100"}`} data-testid={`rate-tab-${c}`}>
              {c}
            </button>
          ))}
          {list.length > tabs.length && (
            <select value={tabs.includes(commodity) ? "" : commodity} onChange={(e) => e.target.value && setCommodity(e.target.value)} className="px-2 py-1.5 rounded text-xs font-bold text-stone-700 bg-white" data-testid="rate-more">
              <option value="">More…</option>
              {list.slice(tabs.length).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>
      </div>

      {list.length === 0 && (
        <div className="rounded-xl bg-white border border-stone-200 p-6 text-sm text-stone-600">No rates yet. They appear after the first daily import.</div>
      )}

      {list.length > 0 && (
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 rounded-xl bg-white border border-stone-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-stone-500">{chartData.length}-day trend · {commodity}</div>
                <div className="font-display text-3xl font-black text-emerald-900 mt-1">₹{last.toLocaleString("en-IN")}<span className="text-sm text-stone-500 font-normal">/quintal</span></div>
                {chartData.length > 1 && (
                  <div className={`mt-1 text-sm font-semibold flex items-center gap-1 ${pct >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    {pct >= 0 ? <TrendUp weight="bold" size={16} /> : <TrendDown weight="bold" size={16} />} {Math.abs(pct).toFixed(2)}% over {chartData.length} days
                  </div>
                )}
              </div>
            </div>
            <div className="mt-6 h-64">
              {trend ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <XAxis dataKey="d" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} tickLine={false} axisLine={false} width={50} domain={["auto", "auto"]} />
                    <Tooltip formatter={(v) => `₹${v.toLocaleString("en-IN")}`} contentStyle={{ borderRadius: 8, border: "1px solid #e7e5e4" }} />
                    <Line type="monotone" dataKey="v" stroke="#064E3B" strokeWidth={2.5} dot={{ r: 3, fill: "#EA580C" }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 rounded shimmer" />
              )}
            </div>
            {trend && chartData.length < 7 && (
              <p className="text-xs text-stone-500 mt-3">Only {chartData.length} day{chartData.length === 1 ? "" : "s"} of real rates are stored so far. The chart grows by one point every day.</p>
            )}
          </div>

          <div className="rounded-xl bg-emerald-950 text-white p-6">
            <div className="text-xs uppercase tracking-widest font-bold text-orange-400">Month-wise average</div>
            <div className="font-display text-2xl font-black mt-1">{commodity}</div>
            <div className="mt-6 space-y-3">
              {months === null && Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-14 rounded bg-emerald-900/40 shimmer" />)}
              {months?.months?.map((m, i) => (
                <motion.div key={m.month} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }} className="flex items-center justify-between bg-emerald-900/50 rounded-md px-3 py-2.5">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-orange-300">{monthName(m.month)}</div>
                    <div className="text-sm font-bold">₹{m.avg_rate.toLocaleString("en-IN")}</div>
                  </div>
                  <div className="text-right">
                    {m.pct_change !== null && <div className={`text-xs font-bold ${m.pct_change >= 0 ? "text-emerald-300" : "text-red-300"}`}>{m.pct_change >= 0 ? "+" : ""}{m.pct_change}%</div>}
                    <div className="text-[10px] text-emerald-200">{m.days} day{m.days === 1 ? "" : "s"}</div>
                  </div>
                </motion.div>
              ))}
              {months && months.months.length < 2 && (
                <p className="text-xs text-emerald-200">
                  Rates have been collected since {months.since || "today"}. Month-on-month change and season comparison appear as the months go by; nothing here is estimated.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
